import { Grid2, Typography } from '@mui/material';
import React, { FC } from 'react';
import ComapareBox from '../CompareBox';
import { EventDataTrendEventItemWithData } from '@demind-inc/core';
import { EventWithCategory } from '../../../DataEventDashboard';
import { colorGreenBase, colorRedBase, grayDark } from '../../../constants';
import './EventFullView.scss';
import { useWearablesContext } from '../../../../../data-access';
import { useTranslation } from 'react-i18next';

interface EventFullViewProps {
  totalEvents: number;
  totalMeetings: number;
  focusTime: string;
  label: string;
  stressfulEvent: EventDataTrendEventItemWithData;
  drainingEvent: EventDataTrendEventItemWithData;
  boostingEvent: EventDataTrendEventItemWithData;
  eventDiffFormatted: string;
  eventDiffValue: number;
  meetingsDiffFormatted: string;
  meetingsDiffValue: number;
  focusTimeDiffFormatted: string;
  focusTimeDiffValue: number;
}

const EventFullView: FC<EventFullViewProps> = ({
  totalEvents,
  eventDiffFormatted,
  eventDiffValue,
  totalMeetings,
  meetingsDiffFormatted,
  meetingsDiffValue,
  focusTime,
  focusTimeDiffFormatted,
  focusTimeDiffValue,
  label,
  stressfulEvent,
  drainingEvent,
  boostingEvent,
}) => {
  const { t } = useTranslation();
  const { stressDevices } = useWearablesContext();

  const hasStressWearableConnected = stressDevices.length > 0;
  return (
    <div className="data-event__full-view">
      <Grid2 container>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <ComapareBox
            title={t('data.events.totalEvents')}
            value={totalEvents}
            diffFormatted={eventDiffFormatted}
            diffValue={eventDiffValue}
            label={label}
            color={
              eventDiffValue > 0 ? colorGreenBase : eventDiffValue < 0 ? colorRedBase : grayDark
            }
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <ComapareBox
            title={t('data.events.totalMeetings')}
            value={totalMeetings}
            diffFormatted={meetingsDiffFormatted}
            diffValue={meetingsDiffValue}
            label={label}
            color={
              meetingsDiffValue > 0
                ? colorGreenBase
                : meetingsDiffValue < 0
                  ? colorRedBase
                  : grayDark
            }
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <ComapareBox
            title={t('data.events.focusTime')}
            value={focusTime}
            diffFormatted={focusTimeDiffFormatted}
            diffValue={focusTimeDiffValue}
            label={label}
            color={
              focusTimeDiffValue > 0
                ? colorGreenBase
                : focusTimeDiffValue < 0
                  ? colorRedBase
                  : grayDark
            }
          />
        </Grid2>
      </Grid2>
      <Grid2 container mt={1}>
        <Grid2 size={{ xs: 12, md: 6 }} p={0.25}>
          <div className="data-event__full-view__stats">
            <Typography variant="body1">{t('data.events.mostEnergyBoostingEvent')}</Typography>
            {boostingEvent ? (
              <EventWithCategory
                className="data-event__inner-box"
                event={boostingEvent.event}
                color={boostingEvent.score >= 0 ? colorGreenBase : colorRedBase}
                score={boostingEvent.score}
              />
            ) : (
              <div className="data-event__inner-box">
                <Typography variant="caption" className="data-event__inner-box__no-data">
                  {t('data.common.noData')}
                </Typography>
              </div>
            )}
          </div>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }} p={0.25}>
          <div className="data-event__full-view__stats">
            <Typography variant="body1">{t('data.events.mostEnergyDrainingEvent')}</Typography>
            {drainingEvent ? (
              <EventWithCategory
                className="data-event__inner-box"
                event={drainingEvent.event}
                color={drainingEvent.score >= 0 ? colorGreenBase : colorRedBase}
                score={drainingEvent.score}
              />
            ) : (
              <div className="data-event__inner-box">
                <Typography variant="caption" className="data-event__inner-box__no-data">
                  {t('data.common.noData')}
                </Typography>
              </div>
            )}
          </div>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }} p={0.25}>
          <div className="data-event__full-view__stats">
            <Typography variant="body1">{t('data.events.mostStressfulEvent')}</Typography>
            {!hasStressWearableConnected ? (
              <div className="data-event__inner-box">
                <Typography variant="caption">
                  {t('data.events.noAvailableDevicesConnected')}
                </Typography>
              </div>
            ) : stressfulEvent ? (
              <EventWithCategory
                className="data-event__inner-box"
                event={stressfulEvent.event}
                color={stressfulEvent.score >= 0 ? colorGreenBase : colorRedBase}
                score={stressfulEvent.score}
              />
            ) : (
              <div className="data-event__inner-box">
                <Typography variant="caption" className="data-event__inner-box__no-data">
                  {t('data.common.noData')}
                </Typography>
              </div>
            )}
          </div>
        </Grid2>
      </Grid2>
    </div>
  );
};

export default EventFullView;
